import React, { useMemo } from 'react';
import { useStore, useStorePrimitive } from '../../hooks/useStore';
import { logger } from '../../services/logger.client';
import { GridStrategy, HexGridStrategy, SquareGridStrategy } from '../../grid/grid-strategy';
import { useGridService } from '../../hooks/useGridService';
import type { RenderedImageRect } from '../../types/types';
import { GridContext, type GridSettings } from './useGrid';
import { DEFAULT_GRID_SETTINGS } from '../../types/types';

interface GridProviderProps {
  imageRect: RenderedImageRect | null;
  children: React.ReactNode;
}

export const GridProvider: React.FC<GridProviderProps> = ({ imageRect, children }) => {
  logger.info('GridProvider: Rendering.');
  const activeMapId = useStorePrimitive(state => state.session?.map?.activeMapId ?? null);
  const allGridSettings = useStore(state => state.settings?.grids);
  const gridService = useGridService(imageRect);

  const gridSettings: GridSettings = useMemo(() => {
    // If there's no active map, there are no grid settings.
    if (activeMapId === null) return DEFAULT_GRID_SETTINGS;
    return allGridSettings?.[activeMapId] ?? DEFAULT_GRID_SETTINGS;
  }, [allGridSettings, activeMapId]);

  const gridOrigin = useMemo(() => {
    if (!imageRect) return { x: 0, y: 0 };
    return {
      x: imageRect.x + (gridSettings.offsetX || 0),
      y: imageRect.y + (gridSettings.offsetY || 0),
    };
  }, [imageRect, gridSettings.offsetX, gridSettings.offsetY]);

  const gridStrategy = useMemo<GridStrategy | null>(() => {
    logger.info({ gridSettingsEnabled: gridSettings.enabled, hasImageRect: !!imageRect }, 'GridProvider: Calculating gridStrategy.');
    if (!gridSettings.enabled || !imageRect) return null;
    if (gridSettings.type === 'hex') return new HexGridStrategy(gridService);
    if (gridSettings.type === 'square') return new SquareGridStrategy(gridService, gridSettings, gridOrigin);
    return null;
  }, [gridSettings, imageRect, gridOrigin, gridService]);

  logger.info({ hasGridStrategy: !!gridStrategy }, 'GridProvider: gridStrategy created.');
  const value = { gridSettings, gridOrigin, gridStrategy, activeMapId, isGridReady: !!gridStrategy };

  return <GridContext.Provider value={value}>{children}</GridContext.Provider>;
};
